import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material'
import { Shuffle, CheckCircle, Upload, Delete, Download } from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { hasPermission } from '../utils/permissions'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import {
  useDrawBracketMutation,
  useGetLatestBracketQuery,
  useConfirmBracketMutation,
  useListRoleSheetsQuery,
  useUploadRoleSheetMutation,
  useDeleteRoleSheetMutation,
  useLazyDownloadRoleSheetQuery,
} from '../store/api/bracketsApi'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { PageHeader } from '../components/PageHeader'
import { getContentPaperSx, getFilterPaperSx } from '../theme/sectionSurfaces'

export default function BracketsPage() {
  const { user } = useAppSelector((s) => s.auth)
  const canManage = user && hasPermission(user.role, 'ASSIGN_CASES')
  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const [caseId, setCaseId] = useState('')

  useEffect(() => {
    if (cases.length && !caseId) setCaseId(cases[0].id)
  }, [cases, caseId])

  const { data: bracket, refetch: refetchBracket } = useGetLatestBracketQuery(caseId, {
    skip: !caseId,
  })
  const { data: sheets = [], refetch: refetchSheets } = useListRoleSheetsQuery(caseId, {
    skip: !caseId,
  })

  const [drawBracket, { isLoading: drawing }] = useDrawBracketMutation()
  const [confirmBracket, { isLoading: confirming }] = useConfirmBracketMutation()
  const [uploadSheet, { isLoading: uploading }] = useUploadRoleSheetMutation()
  const [deleteSheet] = useDeleteRoleSheetMutation()
  const [downloadSheet] = useLazyDownloadRoleSheetQuery()
  const [sheetTitle, setSheetTitle] = useState('')

  const handleDraw = async () => {
    if (!caseId) return
    try {
      await drawBracket({ case_id: caseId }).unwrap()
      refetchBracket()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Bốc thăm thất bại')
    }
  }

  const handleConfirm = async () => {
    if (!bracket?.id) return
    if (!window.confirm('Xác nhận bracket? Sau khi xác nhận không thể chỉnh sửa.')) return
    try {
      await confirmBracket(bracket.id as string).unwrap()
      refetchBracket()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Xác nhận thất bại')
    }
  }

  const handleUpload = async (file: File | null) => {
    if (!file || !caseId) return
    try {
      await uploadSheet({ caseId, title: sheetTitle || undefined, file }).unwrap()
      setSheetTitle('')
      refetchSheets()
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Upload thất bại')
    }
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Bốc thăm & bảng phân vai"
        subtitle="M05: bracket Nguyên đơn / Bị đơn; upload bảng phân vai theo cuộc thi."
        icon={<Shuffle />}
      />

      <Paper elevation={0} sx={(theme) => getFilterPaperSx(theme)}>
        <FormControl fullWidth sx={{ maxWidth: 480 }}>
          <InputLabel>Cuộc thi</InputLabel>
          <Select
            label="Cuộc thi"
            value={caseId}
            onChange={(e) => setCaseId(e.target.value as string)}
          >
            {cases.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {formatCaseDisplay(c)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 3, mb: 3 })}>
        <Typography variant="h6" gutterBottom>
          Bracket
        </Typography>
        {!canManage && (
          <Alert severity="info">Chỉ BTC (quyền phân công cuộc thi) mới bốc thăm / xác nhận.</Alert>
        )}
        {canManage && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Shuffle />}
              onClick={handleDraw}
              disabled={drawing || !caseId}
            >
              Bốc thăm lại
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleConfirm}
              disabled={confirming || !bracket?.id || bracket.status === 'confirmed' || !bracket.id}
            >
              Xác nhận bracket
            </Button>
          </Box>
        )}
        {!bracket && <Alert severity="warning">Chưa có bracket cho cuộc thi này.</Alert>}
        {bracket && (
          <>
            <Chip
              label={bracket.status === 'confirmed' ? 'Đã khóa (confirmed)' : 'Bản nháp'}
              color={bracket.status === 'confirmed' ? 'success' : 'default'}
              sx={{ mb: 2 }}
            />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Nguyên đơn</TableCell>
                  <TableCell>Bị đơn</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bracket.matches.map((m, idx) => {
                  const tp = teams.find((t) => t.id === m.team_plaintiff_id)
                  const td = teams.find((t) => t.id === m.team_defendant_id)
                  return (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{tp?.name || m.team_plaintiff_id}</TableCell>
                      <TableCell>{td?.name || m.team_defendant_id}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>

      <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 3 })}>
        <Typography variant="h6" gutterBottom>
          Bảng phân vai
        </Typography>
        {canManage && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end', mb: 2 }}>
            <TextField
              label="Tiêu đề (tuỳ chọn)"
              value={sheetTitle}
              onChange={(e) => setSheetTitle(e.target.value)}
              size="small"
            />
            <Button variant="outlined" component="label" startIcon={<Upload />} disabled={uploading || !caseId}>
              Upload PDF
              <input
                type="file"
                hidden
                accept=".pdf,application/pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  void handleUpload(f || null)
                }}
              />
            </Button>
          </Box>
        )}
        <List dense>
          {sheets.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Chưa có bảng phân vai.
            </Typography>
          )}
          {sheets.map((s) => (
            <ListItem
              key={s.id}
              secondaryAction={
                <Box>
                  <IconButton
                    edge="end"
                    aria-label="download"
                    onClick={async () => {
                      try {
                        const blob = await downloadSheet(s.id!).unwrap()
                        const url = window.URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = s.file_name || 'role-sheet.pdf'
                        document.body.appendChild(a)
                        a.click()
                        window.URL.revokeObjectURL(url)
                        document.body.removeChild(a)
                      } catch (e: any) {
                        alert(e?.data?.detail || e?.message || 'Tải thất bại')
                      }
                    }}
                  >
                    <Download />
                  </IconButton>
                  {canManage && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={async () => {
                        if (!window.confirm('Xóa bảng phân vai?')) return
                        try {
                          await deleteSheet(s.id!).unwrap()
                          refetchSheets()
                        } catch (e: any) {
                          alert(e?.data?.detail || e?.message)
                        }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              }
            >
              <ListItemText primary={s.title} secondary={s.file_name} />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Container>
  )
}
